import type { SpinChange } from '../../../Generated/SpinChange.ts'
import type { SpinLineageStep } from '../../../Generated/SpinLineageStep.ts'
import type { TaxonomyLadder } from '../../../Generated/TaxonomyLadder.ts'
import { RANK_LABELS, cleanName, headline } from './spinSummary.logic.js'

/** Why a step minted a new SPIN, for people who never saw the clustering. */
export const driverLabel = (c: SpinChange): string => {
  switch (c.driver) {
    case 'MERGE':
      return `Merged ${c.mergedCount} SPINs`
    case 'REANNOTATION':
      return 'Re-annotated against newer reference databases'
    case 'IDENTITY_SCHEME':
      return 'Identity scheme upgrade, no scientific change'
    case 'PROTOTYPE':
      return 'Prototype build'
    case 'NEW_REPRESENTATIVE':
      return c.sequenceChanged ? 'Cluster grew: new representative sequence' : 'Cluster grew: new representative, same sequence'
    case 'REFRESHED':
      return 'Annotation refreshed, same members and sequence'
    case 'OTHER':
      return 'Reclustered'
  }
}

const rankName = (rank?: number) => (rank == null ? '' : RANK_LABELS[rank]!.toLowerCase())
const by = (sources?: string[]) => (sources?.length ? ` (${sources.join(', ')})` : '')

/** What the step changed in the identification. */
export const effectLabel = (c: SpinChange): string => {
  switch (c.effect) {
    case 'IDENTIFICATION_CHANGED':
      return `${RANK_LABELS[c.effectRank ?? 0]} changed${by(c.effectSources)}`
    case 'RESOLVED_DEEPER':
      return `Resolved to ${rankName(c.effectRank)}${by(c.effectSources)}`
    case 'RESOLUTION_LOST':
      return `${RANK_LABELS[c.effectRank ?? 0]} no longer resolved${by(c.effectSources)}`
    case 'ALT_HITS_ONLY':
      return 'Alternative SPARK updated'
    case 'FORMAT_ONLY':
      return 'Formatting only'
    case 'UNCHANGED':
      return 'Taxonomy unchanged'
  }
}

/** Secondary remarks: weak top hit, RDP bootstrap noise. */
export const changeNotes = (c: SpinChange): string[] => [
  ...(c.lowIdentityHit ? ['Weak top hit (< 90% identity), expected to drift'] : []),
  ...(c.rdpChanged ? [c.rdpLowConfidence ? 'RDP low-confidence call changed' : 'RDP call changed'] : [])
]

/** The deepest named rank of a source's top hit, e.g. "Species: Acleris forskaleana". */
export const deepestOf = (ladders: TaxonomyLadder[] | undefined, source: string) => {
  const ranks = ladders?.find((l) => l.source === source && l.hitRank === 0)?.ranks ?? []
  for (let r = ranks.length - 1; r >= 0; r--) {
    const name = cleanName(ranks[r])
    if (name) return { rank: r, name }
  }
  return undefined
}

export interface SourceDiff {
  source: string
  before?: { rank: number; name: string }
  after?: { rank: number; name: string }
  status: 'changed' | 'deeper' | 'shallower' | 'unchanged'
}

/** Per source, the deepest name before and after the step. */
export const sourceDiffs = (step: SpinLineageStep, previous: SpinLineageStep): SourceDiff[] => {
  const sources = [...new Set([...(step.taxonomies ?? []), ...(previous.taxonomies ?? [])].map((l) => l.source))].sort()
  return sources.map((source) => {
    const before = deepestOf(previous.taxonomies, source)
    const after = deepestOf(step.taxonomies, source)
    const status: SourceDiff['status'] =
      (before?.rank ?? -1) < (after?.rank ?? -1)
        ? 'deeper'
        : (before?.rank ?? -1) > (after?.rank ?? -1)
          ? 'shallower'
          : before?.name.toLowerCase() === after?.name.toLowerCase()
            ? 'unchanged'
            : 'changed'
    return { source, before, after, status }
  })
}

/** "Genus resolved" for the step's own display taxon. */
export const resolutionOf = (step: SpinLineageStep): string => {
  const depth = step.display?.depth
  return depth == null ? 'Unresolved' : `${RANK_LABELS[depth]} resolved`
}

export const headlineOf = (step: SpinLineageStep) => headline(step.display)
